// Admin → Reports (spec "App reports"): what the apps sent so their problems can be found and
// fixed: crashes (the system's report, sent the next time the app runs) and problems (Help ›
// Report a Problem…, or a terminal the app saw stop drawing), each with the app's own log.
// Opening one shows its text and log, to read or copy whole.

import { Fragment, useCallback, useEffect, useState } from "react";
import { admin, type AppReport } from "../api";
import { ago, fullDate } from "./Shell";
import { Tag } from "../ui/Tag";
import { Empty } from "../ui/ds";

const TYPE: Record<AppReport["type"], { label: string; tone: "red" | "amber" }> = {
  crash: { label: "crash", tone: "red" },
  problem: { label: "problem", tone: "amber" },
};

function Full({ id }: { id: string }) {
  const [r, setR] = useState<AppReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void admin.report(id).then((x) => (x.ok ? setR(x.data) : setError(x.error)));
  }, [id]);
  if (error) return <div className="form-error" role="alert">{error}</div>;
  if (!r) return <div className="skeleton" aria-busy="true" aria-label="Loading the report" />;
  return (
    <div className="report-full" data-testid="report-full">
      <div className="muted small">
        {r.app} {r.version} ({r.build}) · {r.os} · {r.device} · happened {fullDate(r.at)}
      </div>
      <h3 className="small">Report</h3>
      <pre className="report-text" data-testid="report-text">{r.report || "—"}</pre>
      <h3 className="small">The app's log</h3>
      <pre className="report-text" data-testid="report-log">{r.log || "—"}</pre>
    </div>
  );
}

export function AdminReports() {
  const [reports, setReports] = useState<AppReport[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await admin.reports();
    if (r.ok) setReports(r.data.reports ?? []);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card" id="reports" aria-labelledby="reports-title" data-testid="admin-reports">
      <div className="card-head">
        <div className="card-title">
          <h2 id="reports-title">Reports</h2>
          <span className="count">{reports?.length ?? ""}</span>
        </div>
      </div>
      <p className="muted small">
        Crashes and problems the apps sent, each with the app's log. Admins get an email for each new kind.
      </p>
      <div className="table-wrap table-cards">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">What</th>
              <th scope="col">App</th>
              <th scope="col">From</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {(reports ?? []).map((r) => (
              <Fragment key={r.id}>
                <tr
                  className="row-link"
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                  aria-expanded={open === r.id}
                  data-testid="report-row"
                  data-type={r.type}
                >
                  <td className="cell-head">
                    <Tag tone={TYPE[r.type].tone}>{TYPE[r.type].label}</Tag>{" "}
                    <span className="cell-strong">{r.kind}</span>
                    <div className="muted small report-reason">{r.reason}</div>
                  </td>
                  <td data-label="App">
                    {r.app} {r.build}
                  </td>
                  <td data-label="From">{r.email}</td>
                  <td className="muted" data-label="When" title={fullDate(r.received)}>
                    {ago(r.received)}
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="manage-row">
                    <td colSpan={4}>
                      <Full id={r.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {reports && !reports.length && (
          <Empty size="md" art="tray" className="table-empty" title="No reports" lead="Crashes and problems the apps send show up here." />
        )}
        {!reports && <div className="skeleton" aria-busy="true" aria-label="Loading reports" />}
      </div>
    </section>
  );
}
